import { AfterContentInit, ChangeDetectorRef, Component, forwardRef, Input, OnInit } from "@angular/core";
import {
	AbstractControl,
	ControlValueAccessor,
	FormArray,
	FormControl,
	FormGroup,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ValidationErrors,
	Validator,
	Validators,
} from "@angular/forms";
import { CustomValidators } from "@app/_validators/custom-validators";
import { MaterialScaleVariable } from "@app/animations/fade";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Logger } from "@timeghost/web-api";
import { addDays, parse as parseFromString, startOfISOWeek, startOfToday, startOfWeek } from "date-fns/esm";
import { combineLatest, map, Subject, startWith } from "rxjs";
import { validateOverlapItems } from "../validators";
export interface TargetHourDayValue {
	day: number;
	enabled: boolean;
	items: { from: string; to: string; name?: string; editing?: boolean }[];
}
const log = new Logger("TargetHourDayControlComponent");
@UntilDestroy()
@Component({
	selector: "tg-target-hour-day-control",
	templateUrl: "./target-hour-day-control.component.html",
	styleUrls: ["./target-hour-day-control.component.scss"],

	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => TargetHourDayControlComponent),
			multi: true,
		},
		{ provide: NG_VALIDATORS, useExisting: forwardRef(() => TargetHourDayControlComponent), multi: true },
	],
	animations: [MaterialScaleVariable(0.95, "expandItems")],
})
export class TargetHourDayControlComponent implements OnInit, AfterContentInit, ControlValueAccessor, Validator {
	private readonly _control = new FormGroup(
		{
			enabled: new FormControl(false),
			day: new FormControl(0),
			items: new FormArray([]),
		},
		[validateOverlapItems()]
	);
	get control(): typeof this._control {
		return this._control;
	}
	readonly value$ = this.control.valueChanges.pipe(
		startWith(this.control.value),
		untilDestroyed(this),
		map((d) => {
			return {
				...d,
				dayFormat: addDays(startOfISOWeek(new Date()), d.day),
			};
		})
	);
	readonly onChange$ = new Subject<void>();
	readonly stats$ = combineLatest([this.control.valueChanges, this.onChange$.asObservable()]).pipe(
		untilDestroyed(this),
		startWith([this.control.value]),

		map(() => {
			const value = this.control.value;
			if (!value || !value.items.length) return null;
			const total =
				(value.items as TargetHourDayValue["items"])?.reduce((acc, r) => {
					const start = parseFromString(r.from, "HH:mm", new Date()),
						end = parseFromString(r.to, "HH:mm", new Date()),
						ms = end.getTime() - start.getTime();
					return (acc += ms / 1000);
				}, 0) ?? 0;
			return {
				total,
			};
		})
	);
	@Input()
	day: number = 0;
	propagateChange: Function;
	propagateTouched: Function;
	constructor(private cdr: ChangeDetectorRef) {}
	validate(control: AbstractControl<any, any>): ValidationErrors {
		return this.control?.errors;
	}
	registerOnValidatorChange?(fn: () => void): void {
		// throw new Error('Method not implemented.');
	}
	ngAfterContentInit(): void {
		// this.cdr.detectChanges();
		// this.control.updateValueAndValidity();
	}
	private initialRender = true;
	writeValue(value: TargetHourDayValue): void {
		if (value.items.length && value.items.length > this.control.controls.items.length) {
			this.control.controls.items.clear({ emitEvent: false });
			value.items.reduce((acc: FormArray, r) => {
				acc.push(
					new FormGroup(
						{
							name: new FormControl(r.name || "", { updateOn: "change" }),
							from: new FormControl(r.from, [
								Validators.required,
								CustomValidators.validDateFormat("HH:mm"),
							]),
							to: new FormControl(r.to, [Validators.required, CustomValidators.validDateFormat("HH:mm")]),
							editing: new FormControl(r.editing ?? false),
						},
						{
							validators: [CustomValidators.timeDiffCheck("from", "to")],
							updateOn: "blur",
						}
					),
					{ emitEvent: false }
				);
				return acc;
			}, this.control.controls.items);
		} else if (value.items.length <= 0) this.control.controls.items.clear({ emitEvent: false });
		this.control.patchValue(value, { emitEvent: false });
	}
	get rawValue() {
		return this.control.getRawValue();
	}
	get items() {
		return this.control.get("items");
	}
	registerOnChange(fn: any): void {
		this.propagateChange = (...args: any) => {
			this.onChange$.next();
			return fn(...args);
		};
	}
	registerOnTouched(fn: any): void {
		this.propagateTouched = (...args: any) => {
			this.onChange$.next();
			return fn(...args);
		};
	}
	getDurationFromItem({ from, to }: { from: string; to: string }) {
		const now = startOfToday();
		const start = parseFromString(from, "HH:mm", now),
			end = parseFromString(to, "HH:mm", now);
		if (start > end) return null;
		return end.getTime() - start.getTime();
	}
	setDisabledState?(isDisabled: boolean): void {
		this.control[isDisabled ? "disable" : "enable"]();
		this.items[isDisabled ? "disable" : "enable"]();
	}
	get disabled() {
		return this.control.disabled;
	}
	set disabled(val: boolean) {
		this.control[val ? "disable" : "enable"]();
	}
	toggleCard(enabled?: boolean) {
		if (this.control.disabled) return;
		this.control.patchValue({
			enabled,
		} as TargetHourDayValue);
	}
	onAddTime() {
		if (this.control.disabled) return;
		this.control.controls.items.push(
			new FormGroup(
				{
					name: new FormControl(""),
					from: new FormControl("09:00", [Validators.required, CustomValidators.validDateFormat("HH:mm")]),
					to: new FormControl("17:00", [Validators.required, CustomValidators.validDateFormat("HH:mm")]),
					editing: new FormControl(true),
				},
				[CustomValidators.timeDiffCheck("from", "to")]
			)
		);
		this.pushChange(this.control.getRawValue());
	}
	saveItem(itemIndex: number) {
		if (this.control.disabled) return;
		const ctrl = this.control.controls.items.controls[itemIndex];
		if (!ctrl) return;
		ctrl.patchValue({
			editing: false,
		});
		this.control.updateValueAndValidity();
	}
	editItem(itemIndex: number) {
		const ctrl = this.control.controls.items.controls[itemIndex];
		if (!ctrl) return;
		ctrl.patchValue({
			editing: !ctrl.value.editing,
		});
		this.control.updateValueAndValidity();
	}
	patchValue(value: any) {
		const prev = this.control.value;
		return this.control.patchValue({ ...prev, ...value });
	}
	delItem(itemIndex: number) {
		if (this.control.disabled) return;
		this.control.controls.items.removeAt(itemIndex);
		this.pushChange(this.control.getRawValue());
	}
	parseTimeToDate(time: string) {
		const day = addDays(startOfISOWeek(new Date()), this.control.value.day);
		return parseFromString(time, "HH:mm", day);
	}
	private pushChange(value: any) {
		this.propagateChange?.(value);
		this.propagateTouched?.(value);
		this.writeValue(value as any);
	}
	ngOnInit(): void {
		this.control.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
			this.propagateChange?.(val);
			this.propagateTouched?.(val);
			this.writeValue(val as any);
			log.debug("schedule-day-control", val.day, structuredClone(val));
		});
	}
	readonly now = startOfWeek(new Date(), { weekStartsOn: 1 });
}
